import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  Inject,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChildren,
  NgZone
} from '@angular/core'
import { combineLatest, fromEvent, merge, Observable, ReplaySubject, Subject, timer } from 'rxjs'
import {
  delay,
  filter,
  map,
  mapTo,
  flatMap,
  scan,
  shareReplay,
  take,
  takeUntil,
  takeWhile,
  withLatestFrom,
  switchMap,
  distinctUntilChanged,
  startWith,
  mergeMap,
  skipWhile
} from 'rxjs/operators'
import { IVolumeControls } from '../models'
import Hls, { FragParsedData } from 'hls.js'
import { VideoAdsService } from '../services/video-ads.service'
import { YOUBORA_IMA_ADAPTER } from '../../shared/analytics/directives/video-analytics.directive'
import { IVideoLoggingPostBody } from '../services/video-event-logging.service'
import { VideoErrorComponent } from './video-error/video-error.component'
import * as utilityFunctions from '../functions/utility-functions'
import { DOCUMENT } from '@angular/common'
import { SegmentTrackLiveAds } from 'src/app/shared/interfaces/segment-track-live-ads.interface'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { VideoPlayerMeta } from '../models/stream-tile'
import { PubmaticService } from '../../shared/ads/advertising-service/pubmatic.service'
import { isValidAdEvent, notNullOrUndefined } from '../../shared/functions/type-guards'
import { UserIdentifierService } from '../../shared/ads/advertising-service/user-identifier.service'
import { normalizeVolume } from '../../shared/ads/ad-volume.utility'
import { LocationService } from '../../singleton-services/location.service'

@Component({
  selector: 'flo-video-ad',
  templateUrl: './video-ads.component.html'
})
export class VideoAdsComponent implements OnChanges, OnDestroy, AfterViewInit {
  constructor(
    private readonly zone: NgZone,
    private readonly renderer2: Renderer2,
    private readonly locationService: LocationService,
    private readonly videoAdsService: VideoAdsService,
    private readonly segmentService: SegmentService,
    private readonly pubmaticService: PubmaticService,
    private readonly userIdentifierService: UserIdentifierService,
    @Inject(YOUBORA_IMA_ADAPTER) private readonly adAnalyticsAdapter: any,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  @Input()
  set analyticsPlugin(analyticsPlugin: any) {
    this._analyticsPlugin.next(analyticsPlugin)
  }

  // Triggered when resetAds(true) is called
  private readonly adsReset$ = new Subject<void>()

  private readonly ngAfterViewInit$ = new Subject<void>()

  private readonly ngOnDestroy$ = new Subject<void>()

  private readonly ngOnChanges$ = new Subject<SimpleChanges>()

  private readonly _analyticsPlugin = new ReplaySubject<any>(1)
  private readonly analyticsPlugin$ = this._analyticsPlugin
  private readonly hashEmail$ = this.userIdentifierService.hashEmail$.pipe(take(1))

  public readonly adContainerId = `ad-container-${Math.floor(Math.random() * 10000)}`

  @ViewChildren('adContainerElement')
  public adContainerElement: QueryList<ElementRef<HTMLElement>>

  @ViewChildren(VideoErrorComponent, { read: ElementRef }) private _videoErrorComponent: QueryList<
    ElementRef<VideoErrorComponent>
  >

  @Output()
  public readonly adEvent$ = new Subject<string>()

  @Input()
  public readonly volumeControls?: IVolumeControls
  private readonly volumeControls$ = this.ngOnChanges$.pipe(
    utilityFunctions.mapChanges<IVolumeControls>('volumeControls')
  )

  @Input()
  public readonly contentVideoElement: HTMLVideoElement
  private readonly contentVideoElement$ = this.ngOnChanges$.pipe(
    utilityFunctions.mapChanges<HTMLVideoElement>('contentVideoElement')
  )

  @Input()
  public readonly videoAdLogPartial: object
  private readonly videoAdLogPartial$ = this.ngOnChanges$.pipe(utilityFunctions.mapChanges<any>('videoAdLogPartial'))

  @Input()
  public readonly authenticationToken?: string
  private readonly authenticationToken$ = this.ngOnChanges$.pipe(
    utilityFunctions.mapChanges<any>('authenticationToken')
  )

  @Input()
  public readonly hlsInstance?: Hls
  private readonly hlsInstance$ = this.ngOnChanges$.pipe(utilityFunctions.mapChanges<Hls>('hlsInstance'))

  private readonly videoMeta$ = new ReplaySubject<VideoPlayerMeta>(1)
  @Input() public set videoMeta(videoMeta: VideoPlayerMeta) {
    if (videoMeta) {
      this.videoMeta$.next(videoMeta)
    }
  }

  private readonly adParams$ = new ReplaySubject<Record<string, unknown>>(1)
  @Input() public set adParams(adParams: Record<string, unknown>) {
    if (adParams) {
      this.adParams$.next(adParams)
    }
  }

  private readonly openwrapScript$ = this.ngAfterViewInit$.pipe(
    map(() => this.pubmaticService.pwt),
    filter(notNullOrUndefined),
    take(1),
    shareReplay(1)
  )

  public videoCurrentTime$ = this.contentVideoElement$.pipe(
    switchMap(contentVideoElement => fromEvent(contentVideoElement, 'timeupdate')),
    map(event => event.target),
    map((target: HTMLVideoElement) => target.currentTime),
    takeUntil(this.ngOnDestroy$)
  )

  public readonly fragmentData$ = this.hlsInstance$.pipe(
    take(1),
    utilityFunctions.flatMapFragParsingData<[string, FragParsedData]>(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  /**
   * Functions deep diving into the fragParsingData sent back from hls.js
   * at this point the cueOutTag$ will have filtered out only the array of the cue-out tag
   * comes back as this structure [[CUE-OUT, DURATION=30]] so need we need to map and grab just the duration string.
   * Also Filtering out Ad Event Status from Cue Out Tag.
   */

  public readonly cueOutFragment$ = this.fragmentData$.pipe(
    filter(fragData => !!fragData[1]),
    filter(fragData => !!fragData[1].frag.type && fragData[1].frag.type === 'main'),
    map(fragData => (utilityFunctions.fragIncludesCueOutTag(fragData[1]) ? fragData : [])),
    filter(res => !!res.length),
    shareReplay(1)
  )

  public readonly cueOutTag$ = this.cueOutFragment$.pipe(
    utilityFunctions.safeMap<FragParsedData>(event => event[1]),
    map(videoFrag => videoFrag.frag),
    map(frag => frag.tagList),
    map(tagList => tagList.filter(tags => utilityFunctions.checkAdMarkersFromCueOutTag(tags))),
    filter(res => !!res.length),
    shareReplay(1)
  )

  /**
   * Pull out the 'EXT-X-ASSET' tag's value from the fragment list if it exists
   *
   * Example fragment list:
   * [
   *   ['OTHER-VAL', 'SOMETHING'],
   *   ['EXT-X-ASSET', 'eventStatus=ADS-PRE'], <-- (optional)
   *   ['EXT-X-CUE-OUT', 'DURATION=30']
   * ]
   */
  public readonly assetTag$ = this.cueOutFragment$.pipe(
    utilityFunctions.safeMap<FragParsedData>(event => event[1]),
    map(videoFrag => videoFrag.frag),
    map(frag => frag.tagList),
    map(tagList => tagList.filter(tags => tags.includes('EXT-X-ASSET'))[0]),
    map(assetTagTuple => (assetTagTuple && assetTagTuple.length === 2 ? assetTagTuple[1] : '')),
    shareReplay(1)
  )

  public readonly adEventStatus$ = this.assetTag$.pipe(
    map(utilityFunctions.getEventStatusFromAssetTag),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public readonly adDuration$ = this.cueOutTag$.pipe(
    filter(tagList => !!tagList[0]),
    map(tagList => tagList[0]),
    filter(cueOutTag => !!cueOutTag[1]),
    map(cueOutTag => cueOutTag[1]),
    map(utilityFunctions.getAdDurationFromCueOutTag),
    shareReplay(1)
  )

  private owAdParams$: Observable<Record<string, unknown>> = combineLatest([
    this.adParams$.pipe(distinctUntilChanged()),
    this.adEventStatus$,
    this.adDuration$.pipe(distinctUntilChanged()),
    this.hashEmail$,
    this.volumeControls$
  ]).pipe(
    map(([adParams, adEventStatus, adDuration, hashEmail, volumeControls]) => {
      const adParamsWithEventStatus = { ...adParams, event_status: adEventStatus }
      const custParams = utilityFunctions.formatCustomParams(adParamsWithEventStatus)
      const description = this.locationService.location
      const vpMuteState = volumeControls.isMuted ? '1' : '0'
      return {
        impl: 's',
        pmnd: '0',
        pmad: `${Math.ceil(+adDuration / 15)}`,
        ciu_szs: '300x250',
        pod: `${Date.now()}`,
        plcmt: 1,
        pmxd: `${Number(adDuration) * 1000}`,
        cust_params: custParams,
        ppid: `${hashEmail}`,
        description_url: description,
        vpmute: vpMuteState
      }
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  private videoAdUnit$ = this.adDuration$.pipe(switchMap(adDuration => this.videoAdsService.videoAdUnit$(adDuration)))

  private readonly owBids$ = combineLatest([this.videoAdUnit$, this.contentVideoElement$]).pipe(
    map(([videoAdUnit, videoElement]) => this.pubmaticService.formatOwBidConfig(videoElement, videoAdUnit)),
    shareReplay(1)
  )

  private owRequestBidsUrl$ = combineLatest([this.owBids$, this.owAdParams$]).pipe(
    withLatestFrom(this.openwrapScript$),
    mergeMap(([[owBids, owParams], owScript]) => this.pubmaticService.generateDFPUrl$(owBids, owScript, owParams)),
    distinctUntilChanged(),
    shareReplay(1)
  )

  /**
   * When adStarted fires we show the timer and commercial break messaging behind the ad
   */
  public adTimer$ = combineLatest([this.adDuration$, this.adEvent$]).pipe(
    filter(([adTime, event]) => event === 'adStarted'),
    flatMap(([adTime]) => this.countDownAdTime(adTime)),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  /**
   * When timer is over ensure that we put the live stream back into view. This is subscribed to in the handleAdStop stream
   */
  public adTimeEnded$ = this.adTimer$.pipe(
    map(time => time === 0),
    filter(Boolean),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adContainerElement$ = this.ngAfterViewInit$.pipe(
    map(() => this.adContainerElement),
    map(queryList => queryList.first),
    map(elementRef => elementRef.nativeElement),
    take(1),
    shareReplay(1)
  )

  private readonly videoErrorComponentRef$ = this.ngAfterViewInit$.pipe(
    map(() => this._videoErrorComponent),
    map(queryList => queryList.first),
    map(elementRef => elementRef.nativeElement),
    take(1),
    shareReplay(1)
  )

  private readonly adContainer$ = combineLatest([this.adContainerElement$, this.contentVideoElement$]).pipe(
    utilityFunctions.flatMapAdDisplayContainer(this.videoAdsService),
    take(1),
    shareReplay(1)
  )

  private readonly adsLoader$ = this.adContainer$.pipe(
    utilityFunctions.flatMapAdsLoader(this.videoAdsService),
    take(1),
    withLatestFrom(this.contentVideoElement$),
    utilityFunctions.registerContentCompleteListener(),
    utilityFunctions.mapGetHead(),
    shareReplay(1)
  )

  private readonly adsManagerLoadedEvent$ = this.adsLoader$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdsManagerLoaded(),
    shareReplay(1)
  )

  private readonly adsManager$ = this.adsManagerLoadedEvent$.pipe(
    withLatestFrom(this.contentVideoElement$),
    utilityFunctions.mapGetAdsManager(),
    shareReplay(1)
  )

  public readonly initializeAdAnalytics = combineLatest([this.analyticsPlugin$, this.adsManager$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([plugin, adsManager]) => {
      this.zone.runOutsideAngular(() => {
        plugin.setAdsAdapter(new this.adAnalyticsAdapter(adsManager))
      })
    })

  private readonly adsLoaderError$ = this.adsLoader$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdErrors()
  )

  private readonly pauseContentRequest$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentPauseRequested(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adStartedEvent$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    flatMap(([adsManager, videoAdsLibrary]) =>
      utilityFunctions.fromAdsEvent(adsManager, videoAdsLibrary.AdEvent.Type.STARTED)
    ),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly resumeContentRequest$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentResumeRequested(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly allAdsCompleted$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAllAdsCompleted(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adCompleted$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdCompleted(),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly isAdPlaying$ = merge(
    this.adStartedEvent$.pipe(mapTo(true)),
    this.adCompleted$.pipe(mapTo(false)),
    this.allAdsCompleted$.pipe(mapTo(false)),
    this.adTimeEnded$.pipe(mapTo(false)),
    this.adsReset$.pipe(mapTo(false))
  ).pipe(shareReplay(1))

  private readonly videoAdClicked$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdClicked(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdStarted$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdStarted(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly videoAdCompleted$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdCompleted(),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly videoAdPaused$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentPause(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdResumed$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentResume(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdFirstQuartile$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentFirstQuartile(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdMidpoint$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentMidpoint(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly videoAdThirdQuartile$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapContentThirdQuartile(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adImpression$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdImpression(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adVolumeChanged$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapVolumeChanged(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly adsManagerError$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    utilityFunctions.flatMapAdErrors(),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private readonly isAdPaused$ = merge(
    this.videoAdPaused$.pipe(map(() => true)),
    this.videoAdResumed$.pipe(map(() => false))
  )

  private readonly videoAdsEventsList$ = this.adsManager$.pipe(
    withLatestFrom(this.videoAdsService.videoAdsLibrary$),
    map(([_, videoAdsLibrary]) => {
      return utilityFunctions.mapAdEventsToSegment(videoAdsLibrary)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  public readonly logAdImpression = this.adImpression$.subscribe(() => this.adEvent$.next('adImpression'))

  public readonly logAdClicked = this.videoAdClicked$.subscribe(() => this.adEvent$.next('adClick'))

  public readonly logAdEvents = merge(
    this.videoAdCompleted$,
    this.videoAdStarted$,
    this.videoAdClicked$,
    this.videoAdPaused$,
    this.videoAdResumed$,
    this.videoAdFirstQuartile$,
    this.videoAdMidpoint$,
    this.videoAdThirdQuartile$,
    this.adsLoaderError$,
    this.adsManagerError$,
    this.adVolumeChanged$
  )
    .pipe(
      filter(isValidAdEvent),
      withLatestFrom(this.videoAdsEventsList$, this.videoMeta$),
      map(([adEvent, segmentEventsMap, videoMeta]: any) => [adEvent, segmentEventsMap[adEvent.type], videoMeta]),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([adEvent, adEventName, videoMeta]) => {
      const context: SegmentTrackLiveAds = {
        name: 'Live Mid Roll',
        ad_type: videoMeta.eventName,
        creative_ad_id: adEvent.getAd().getCreativeAdId(),
        creative_id: adEvent.getAd().getCreativeId(),
        ad_id: adEvent.getAd().getAdId(),
        action: adEvent.type === 'click' ? 'Click' : undefined
      }
      this.segmentService.trackLiveAd(context, adEventName)
    })

  public readonly isAdResumable = combineLatest([fromEvent(this.document, 'visibilitychange'), this.isAdPaused$])
    .pipe(withLatestFrom(this.adsManager$), takeUntil(this.ngOnDestroy$))
    .subscribe(([[document, isAdPaused], adsManager]: [[any, boolean], google.ima.AdsManager]) => {
      if (document.target.visibilityState === 'visible' && isAdPaused) adsManager.resume()
    })

  public readonly cueOutAdStart$ = this.cueOutFragment$.pipe(
    utilityFunctions.safeMap<FragParsedData>(event => event[1]),
    map(videoFrag => videoFrag.frag),
    map(frag => frag.start)
  )

  public readonly fireAdAtStartTime$ = combineLatest([this.videoCurrentTime$, this.cueOutAdStart$]).pipe(
    map(([currentTime, adStart]) => {
      return Math.floor(adStart) === Math.floor(currentTime)
    }),
    distinctUntilChanged()
  )

  private fireAd$ = combineLatest([this.owRequestBidsUrl$, this.fireAdAtStartTime$]).pipe(
    filter(([adTagUrl, isAdStart]) => !!isAdStart)
  )

  private readonly videoAdLog$ = combineLatest([
    this.videoAdLogPartial$,
    this.fireAd$.pipe(map(([adTagUrl]) => adTagUrl))
  ]).pipe(utilityFunctions.mapToVideoAdLog(), shareReplay(1))

  public readonly requestAd = this.fireAd$
    .pipe(
      map(([adTagUrl]) => adTagUrl),
      withLatestFrom(this.adsLoader$, this.isAdPlaying$.pipe(startWith(false))),
      filter(([adsRequest, adsLoader, isAdPlaying]) => !isAdPlaying),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(
      // tslint:disable: readonly-array
      ([adTagUrl, adsLoader, _isAdPlaying]: [string, google.ima.AdsLoader, boolean]) => {
        const adsRequest = Object.assign(new (google.ima as any).AdsRequest(), {
          adTagUrl,
          linearAdSlotWidth: '100%',
          linearAdSlotHeight: '100%',
          nonLinearAdSlotWidth: '100%',
          nonLinearAdSlotHeight: '100%'
        })
        adsLoader.requestAds(adsRequest)
        this.adEvent$.next('adRequest')
      }
    )

  public readonly normalizeAdVolume = combineLatest([this.adsManager$, this.adVolumeChanged$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([adsManager, _]) => {
      const currentVolume = adsManager.getVolume()
      const normalizedVolume = normalizeVolume(currentVolume, 0.6)
      adsManager.setVolume(normalizedVolume)
      this.adEvent$.next('volumeChanged')
    })

  public readonly logAdEvent = this.adEvent$
    .pipe(withLatestFrom(this.videoAdLog$, this.authenticationToken$), takeUntil(this.ngOnDestroy$))
    .subscribe(([adEvent, videoAdLog, authenticationToken]) => {
      const log = utilityFunctions.getAdEventLog(videoAdLog, adEvent)
      this.videoAdsService.logAdEvent(authenticationToken.id, log)
    })

  debugLogAdEvent = this.cueOutFragment$
    .pipe(withLatestFrom(this.videoAdLog$, this.authenticationToken$), takeUntil(this.ngOnDestroy$))
    .subscribe(([[event, metadata], videoAdLog, authenticationToken]) => {
      try {
        const fragMetadata = metadata && metadata.frag
        const enhancedLog = utilityFunctions.getAdEventLog(
          {
            ...videoAdLog,
            context: {
              ...videoAdLog.context,
              value: { ...videoAdLog.context.value, metadata: JSON.stringify(fragMetadata) }
            }
          },
          `${event} with metadata`
        )
        this.videoAdsService.logAdEvent(authenticationToken.id, enhancedLog)
      } catch (error) {
        const enhancedLog = utilityFunctions.getAdEventLog(
          {
            ...videoAdLog,
            context: { ...videoAdLog.context, value: { ...videoAdLog.context.value, error } }
          },
          `${event} with metadata`
        )
        this.videoAdsService.logAdEvent(authenticationToken.id, enhancedLog)
      }
    })

  public readonly logAdBlock = combineLatest([this.authenticationToken$, this.videoAdLogPartial$])
    .pipe(take(1))
    .subscribe(([authenticationToken, videoAdLog]) =>
      this.videoAdsService.logAdBlock(authenticationToken.id, utilityFunctions.getAdEventLog(videoAdLog, 'adBlock'))
    )

  private handleAdError(
    adErrorEvent: google.ima.AdErrorEvent,
    videoAdLog: IVideoLoggingPostBody,
    authenticationToken: { id: string }
  ): void {
    const error = utilityFunctions.getAdError(adErrorEvent)
    const body = { ...videoAdLog, context: { ...videoAdLog.context, error: error && error.toString() } }
    const log = utilityFunctions.getAdEventLog(body, 'VideoAdsComponent:handleAdError')
    const isFatalError = utilityFunctions.isFatalAdError(adErrorEvent)
    if (isFatalError) {
      // TODO: Log the adCreativeID, utilize logging service w/ level: ERROR
      console.error('adError - call resetAds', adErrorEvent, log)
      this.videoAdsService.logAdEvent(authenticationToken.id, log)
      this.adEvent$.next('adError')
      this.resetAds()
    } else {
      // TODO: utilize logging service w/ level: WARN
      console.warn('adError', adErrorEvent, log)
    }
  }

  public readonly initializeAdsManager = this.adsManager$
    .pipe(
      withLatestFrom(
        this.contentVideoElement$,
        this.authenticationToken$,
        this.videoAdLog$,
        this.videoAdsService.videoAdsLibrary$
      ),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([adsManager, contentVideoElement, token, videoAdLog, videoAdsLibrary]) => {
      try {
        // Initialize the ads manager. Ad rules playlist will start at this time.
        adsManager.init(
          contentVideoElement.clientWidth,
          contentVideoElement.clientHeight,
          videoAdsLibrary.ViewMode.NORMAL
        )

        const contentIsPlaying = !contentVideoElement.paused
        if (contentIsPlaying) {
          // Call start to show ads. Single video and overlay ads will
          // start at this time; this call will be ignored for ad rules, as ad rules
          // ads start when the adsManager is initialized.
          adsManager.start()
        }
      } catch (adError) {
        // An error may be thrown if there was a problem with the VAST response.
        // Play content here, because we won't be getting an ad.
        // Since an error occurred. Ensure that the content video CSS is restored, like stopAd in component

        contentVideoElement.play()

        // takes care of resetting ads and logging
        this.handleAdError(adError, videoAdLog, token)
      }
    })

  public readonly adsErrorHandler = merge(this.adsLoaderError$, this.adsManagerError$)
    .pipe(withLatestFrom(this.videoAdLog$, this.authenticationToken$), takeUntil(this.ngOnDestroy$))
    .subscribe(([adErrorEvent, videoAdLog, authenticationToken]) => {
      this.handleAdError(adErrorEvent as google.ima.AdErrorEvent, videoAdLog, authenticationToken)
    })

  public readonly setAdVolume$ = combineLatest([this.isAdPlaying$, this.volumeControls$])
    .pipe(
      filter(([isAdPlaying]) => isAdPlaying === true),
      withLatestFrom(this.contentVideoElement$, this.adsManager$),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(utilityFunctions.setAdVolume)

  public readonly startAd = this.pauseContentRequest$
    .pipe(
      withLatestFrom(this.adContainerElement$, this.contentVideoElement$, this.videoErrorComponentRef$),
      delay(100),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([_contentPauseRequest, adContainerElement, contentVideoElement, videoErrorElement]) => {
      const renderer2 = this.renderer2
      renderer2.removeClass(adContainerElement, 'd-none')
      renderer2.removeClass(videoErrorElement, 'd-none')
      renderer2.addClass(videoErrorElement, 'd-flex')
      // TODO: Bug causes adsManager.init to receive width=0, height=0. This removes the style Google gives it
      Array.from(adContainerElement.children).forEach((element: HTMLElement) => {
        renderer2.removeAttribute(element, 'style')
      })
      const insertedElements = utilityFunctions.flatten(utilityFunctions.getDescendantElements(adContainerElement))
      insertedElements.forEach((element: HTMLElement) => {
        renderer2.addClass(element, 'ad-video-inserted-element')
      })

      renderer2.setAttribute(contentVideoElement, 'style', '')
      // setting contentVideoElement to display:none automatically pauses the video, but
      // continue playing live content in the background, so set to top and left out of view
      renderer2.addClass(contentVideoElement, 'tiny-playing-video')
      contentVideoElement.muted = true
      this.adEvent$.next('adStarted')
    })

  private stopAds(
    adContainerElement: HTMLElement,
    volumeControls: IVolumeControls,
    contentVideoElement: HTMLVideoElement,
    videoErrorElement: VideoErrorComponent
  ) {
    const renderer2 = this.renderer2
    renderer2.removeClass(contentVideoElement, 'tiny-playing-video')
    renderer2.removeClass(videoErrorElement, 'd-flex')
    renderer2.addClass(videoErrorElement, 'd-none')
    renderer2.addClass(adContainerElement, 'd-none')
    contentVideoElement.muted = volumeControls.isSelectedStream ? volumeControls.isMuted : true
    contentVideoElement.play()

    this.resetAds(false)
    // handle destroying of the ad placeholder
    this.adEvent$.next('adComplete')
  }

  public countDownAdTime = (adTime: number) => {
    // Ad duration comes back in milliseconds, convert it to seconds and add a padding of 5 seconds
    // in case the ad itself buffers. Try to ensure we dont cut the ad off short
    return timer(1000, 1000).pipe(
      scan((acc, curr) => acc - 1, adTime + 5),
      takeWhile(time => time >= 0)
    )
  }

  public readonly handleAdStop = merge(
    this.resumeContentRequest$,
    this.allAdsCompleted$,
    this.adsReset$,
    this.adTimeEnded$,
    this.ngOnDestroy$
  )
    .pipe(
      withLatestFrom(
        this.adContainerElement$,
        this.volumeControls$,
        this.contentVideoElement$,
        this.videoErrorComponentRef$
      )
    )
    .subscribe(
      ([_resumeContentOrAdError, adContainerElement, volumeControls, contentVideoElement, videoErrorElement]) => {
        // Once all ads have played reset the ads manager and get ready for the next ad break.
        this.stopAds(adContainerElement, volumeControls, contentVideoElement, videoErrorElement)
      }
    )

  public readonly initializeVideoAdsService = this.ngAfterViewInit$.pipe(take(1)).subscribe(() => {
    this.videoAdsService.init(this.renderer2)
  })

  public ngAfterViewInit() {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }
  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.ngOnChanges$.complete()
  }
  public ngOnChanges(simpleChanges: SimpleChanges) {
    this.ngOnChanges$.next(simpleChanges)
  }

  private resetAds(triggerAdsReset = true) {
    if (triggerAdsReset) {
      this.adsReset$.next()
    }

    combineLatest([this.adsManager$, this.adsLoader$, this.analyticsPlugin$])
      .pipe(take(1))
      .subscribe(([adsManager, adsLoader, plugin]) => {
        // Destroy the current AdsManager, in case the tag you requested previously
        // contains post-rolls (don't play those now).
        if (adsManager) {
          adsManager.setVolume(0)
          adsManager.destroy()
        }

        // Your AdsLoader will be set up on page-load. You should re-use the same
        // AdsLoader for every request. For more info on setting up the AdsLoader,
        // see the "Get Started" guide in the prerequisites above.
        if (adsLoader) {
          // Reset the IMA SDK.
          adsLoader.contentComplete()
        }

        // Manually fire resume on the ads adapter because we can't rely on Youbora to do so (FLO-13810).
        if (plugin && plugin.getAdapter()) {
          plugin.getAdapter().fireResume()
        }
      })
  }
}
